import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal, ScaleImage } from "@/components/site/Reveal";
import { POSTS } from "@/data/blog";

export default function BlogArticle() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  if (!post) {
    return (
      <div className="pt-44 pb-24 mx-auto max-w-[1500px] px-6 md:px-12" data-testid="blog-not-found">
        <h1 className="font-display font-medium text-4xl tracking-tight">Article not found.</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 font-mono-tech text-[12px] uppercase tracking-widest">
          <ArrowLeft className="h-4 w-4" /> Back to Journal
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-article-page">
      <article className="pt-36 md:pt-44">
        <div className="mx-auto max-w-[900px] px-6 md:px-12">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Journal
          </Link>

          <div className="mt-8 flex items-center gap-4 font-mono-tech text-[11px] uppercase tracking-widest text-muted-foreground">
            <span className="text-[hsl(var(--signal))]">{post.category}</span>
            <span>{post.date}</span>
            <span>{post.readTime}</span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-display font-medium text-4xl md:text-6xl tracking-tight leading-[1.02]"
          >
            {post.title}
          </motion.h1>
        </div>

        <div className="mx-auto max-w-[1200px] px-6 md:px-12 mt-14">
          <ScaleImage src={post.cover} alt={post.title} className="h-[320px] md:h-[560px]" />
        </div>

        <div className="mx-auto max-w-[760px] px-6 md:px-12 py-12 md:py-16">
          {post.body.map((para, i) => (
            <Reveal key={i} delay={0.02}>
              <p className="text-lg md:text-xl leading-[1.75] text-foreground/85 mb-8">
                {para}
              </p>
            </Reveal>
          ))}
          <div className="mt-10 pt-8 border-t border-border font-mono-tech text-[11px] uppercase tracking-widest text-muted-foreground">
            Written by {post.author}
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="py-14 md:py-20 bg-secondary border-t border-border">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-12">
            [ More from the Journal ]
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/blog/${p.slug}`} className="group block" data-testid={`related-post-${p.slug}`}>
                  <ScaleImage src={p.cover} alt={p.title} className="h-56" />
                  <div className="flex items-center gap-3 mt-4 font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span className="text-[hsl(var(--signal))]">{p.category}</span>
                  </div>
                  <h3 className="mt-2 font-display font-medium text-xl tracking-tight leading-tight group-hover:text-muted-foreground transition-colors flex items-start gap-2">
                    {p.title}
                    <ArrowUpRight className="h-4 w-4 mt-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
